import type { StudioMapCamera } from './studioCamera';

export type StudioMapGestureEvent =
  | { type: 'click'; point?: { lat: number; lng: number } }
  | { type: 'drag-start' }
  | { type: 'drag-end' }
  | { type: 'zoom' };

export interface StudioMapFitOptions {
  paddingPx: number;
  fallbackZoom: number;
  maxZoom?: number;
}

/**
 * Narrow camera contract used by Studio's map projection.
 *
 * Tests can inject an in-memory implementation; production wraps the active
 * Google Map. Engineering layout state never crosses this UI-only boundary.
 */
export interface StudioMapViewportAdapter {
  getCenter: () => StudioMapCamera['center'] | null;
  setCenter: (center: StudioMapCamera['center']) => void;
  getZoom: () => number | null;
  setZoom: (zoom: number) => void;
  fit: (
    points: readonly StudioMapCamera['center'][],
    options: StudioMapFitOptions,
  ) => void;
  resize: () => void;
  getContainerElement: () => HTMLElement | null;
  onIdle: (listener: () => void) => () => void;
  onGesture: (listener: (event: StudioMapGestureEvent) => void) => () => void;
}

export function createGoogleMapsStudioMapViewportAdapter(
  map: google.maps.Map,
): StudioMapViewportAdapter {
  return {
    getCenter: () => {
      const center = map.getCenter();
      return center ? { lat: center.lat(), lng: center.lng() } : null;
    },
    setCenter: center => map.setCenter(center),
    getZoom: () => {
      const zoom = map.getZoom();
      return zoom != null && Number.isFinite(zoom) ? zoom : null;
    },
    setZoom: zoom => map.setZoom(zoom),
    fit: (points, options) => {
      if (points.length === 0 || typeof google === 'undefined' || !google.maps) return;
      if (points.length === 1) {
        map.setCenter(points[0]!);
        map.setZoom(Math.min(options.maxZoom ?? options.fallbackZoom, options.fallbackZoom));
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      for (const point of points) bounds.extend(point);
      if (bounds.isEmpty()) return;
      map.fitBounds(bounds, options.paddingPx);
      if (options.maxZoom == null) return;
      const once = map.addListener('idle', () => {
        once.remove();
        const zoom = map.getZoom();
        if (zoom != null && zoom > options.maxZoom!) {
          map.setZoom(options.maxZoom!);
        }
      });
    },
    resize: () => {
      if (typeof google === 'undefined' || !google.maps) return;
      google.maps.event.trigger(map, 'resize');
    },
    getContainerElement: () => map.getDiv()?.parentElement ?? map.getDiv() ?? null,
    onIdle: listener => {
      const handle = map.addListener('idle', listener);
      return () => handle.remove();
    },
    onGesture: listener => {
      const handles = [
        map.addListener('click', (event: google.maps.MapMouseEvent) => {
          const position = event.latLng;
          listener({
            type: 'click',
            ...(position
              ? { point: { lat: position.lat(), lng: position.lng() } }
              : {}),
          });
        }),
        map.addListener('dragstart', () => listener({ type: 'drag-start' })),
        map.addListener('dragend', () => listener({ type: 'drag-end' })),
        map.addListener('zoom_changed', () => listener({ type: 'zoom' })),
      ];
      return () => {
        for (const handle of handles) handle.remove();
      };
    },
  };
}
